import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import EditableListField from './EditableListField';
import './ResumeParserSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const GRADES = ['Junior', 'Middle', 'Senior'];

function ensureArray(arr) {
  return Array.isArray(arr) ? arr : [];
}

function ResumeParserSubTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [extractModalOpen, setExtractModalOpen] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [extractedFile, setExtractedFile] = useState(null);
  const [extractLoading, setExtractLoading] = useState(false);
  const [processLoading, setProcessLoading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingResume, setViewingResume] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingResume, setEditingResume] = useState(null);
  const [editFormData, setEditFormData] = useState({
    profession: '',
    grade: 'Middle',
    years_experience: 0,
    skills: [],
    description: '',
    workplaces: []
  });
  const fileInputRef = useRef(null);

  const fetchResumes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/processed-resumes`);
      setList(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка загрузки: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResumes();
  }, []);

  const handleExtractPdf = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Выберите файл PDF');
      return;
    }
    setExtractLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/api/resume-parser/extract-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setExtractedText(response.data.text || '');
      setExtractedFile(file);
      setExtractModalOpen(true);
    } catch (err) {
      setError('Ошибка извлечения текста: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExtractLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleProcessText = async () => {
    const text = extractedText.trim();
    if (!text) {
      setError('Введите или вставьте текст резюме');
      return;
    }
    setProcessLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('text', text);
      if (extractedFile) {
        formData.append('file', extractedFile);
      }
      await axios.post(`${API_URL}/api/resume-parser/process`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const hadFile = !!extractedFile;
      setExtractModalOpen(false);
      setExtractedText('');
      setExtractedFile(null);
      setSuccessMessage('Обработка прошла успешно. Запись доступна в таблице ниже.' + (hadFile ? ' PDF загружен в S3 и привязан к резюме.' : ''));
      fetchResumes();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError('Ошибка обработки: ' + (err.response?.data?.detail || err.message));
    } finally {
      setProcessLoading(false);
    }
  };

  const handleView = async (item) => {
    try {
      const response = await axios.get(`${API_URL}/api/processed-resumes/${item.id}`);
      setViewingResume(response.data);
      setViewModalOpen(true);
    } catch (err) {
      setError('Ошибка загрузки: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = async (item) => {
    try {
      const response = await axios.get(`${API_URL}/api/processed-resumes/${item.id}`);
      const r = response.data;
      setEditingResume(r);
      setEditFormData({
        profession: r.profession || '',
        grade: r.grade || 'Middle',
        years_experience: Number(r.years_experience) || 0,
        skills: ensureArray(r.skills),
        description: r.description || '',
        workplaces: (r.workplaces || []).map((w) => ({
          duration_months: w.duration_months || 1,
          duties_description: w.duties_description || ''
        }))
      });
      setEditModalOpen(true);
    } catch (err) {
      setError('Ошибка загрузки: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingResume) return;
    const payload = {
      profession: editFormData.profession,
      grade: editFormData.grade,
      years_experience: editFormData.years_experience,
      workplaces_count: editFormData.workplaces.length,
      skills: editFormData.skills,
      description: editFormData.description,
      workplaces: editFormData.workplaces.map((w) => ({
        duration_months: Number(w.duration_months) || 1,
        duties_description: String(w.duties_description || '').trim() || '—'
      }))
    };
    try {
      await axios.put(`${API_URL}/api/processed-resumes/${editingResume.id}`, payload);
      setEditModalOpen(false);
      setEditingResume(null);
      fetchResumes();
      setError(null);
    } catch (err) {
      setError('Ошибка сохранения: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить это обработанное резюме?')) return;
    try {
      await axios.delete(`${API_URL}/api/processed-resumes/${id}`);
      fetchResumes();
    } catch (err) {
      setError('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    }
  };

  const addWorkplace = () => {
    setEditFormData((prev) => ({
      ...prev,
      workplaces: [...prev.workplaces, { duration_months: 1, duties_description: '' }]
    }));
  };

  const removeWorkplace = (index) => {
    setEditFormData((prev) => ({
      ...prev,
      workplaces: prev.workplaces.filter((_, i) => i !== index)
    }));
  };

  const updateWorkplace = (index, field, value) => {
    setEditFormData((prev) => {
      const next = [...prev.workplaces];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, workplaces: next };
    });
  };

  return (
    <div className="resume-parser-sub-tab">
      <div className="tab-header">
        <h2>Парсинг резюме</h2>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="parser-upload-block">
        <h3>Загрузить PDF и обработать</h3>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleExtractPdf}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={extractLoading}
          onClick={() => fileInputRef.current?.click()}
        >
          {extractLoading ? 'Извлечение…' : 'Выбрать PDF и извлечь текст'}
        </button>
      </div>

      {extractModalOpen && (
        <div className="modal-overlay" onClick={() => !processLoading && setExtractModalOpen(false)}>
          <div className="modal-content parser-text-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Текст резюме</h3>
            <p className="modal-hint">При необходимости отредактируйте текст, затем нажмите «Отправить на обработку».</p>
            <textarea
              className="parser-textarea"
              value={extractedText}
              onChange={(e) => setExtractedText(e.target.value)}
              rows={14}
              placeholder="Текст из PDF..."
            />
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" disabled={processLoading} onClick={handleProcessText}>
                {processLoading ? 'Обработка…' : 'Отправить на обработку'}
              </button>
              <button type="button" className="btn btn-secondary" disabled={processLoading} onClick={() => setExtractModalOpen(false)}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {viewModalOpen && viewingResume && (
        <div className="modal-overlay" onClick={() => setViewModalOpen(false)}>
          <div className="modal-content parser-view-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Просмотр резюме</h3>
            <div className="view-content">
              <div className="view-section">
                <h4>Основная информация</h4>
                <div className="view-field">
                  <span className="view-label">Профессия:</span>
                  <span className="view-value">{viewingResume.profession || '—'}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Грейд:</span>
                  <span className="view-value">{viewingResume.grade || '—'}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Лет опыта:</span>
                  <span className="view-value">{Number(viewingResume.years_experience) || '—'}</span>
                </div>
                <div className="view-field">
                  <span className="view-label">Количество мест работы:</span>
                  <span className="view-value">{viewingResume.workplaces_count || 0}</span>
                </div>
              </div>
              <div className="view-section">
                <h4>Навыки</h4>
                <div className="view-skills">
                  {ensureArray(viewingResume.skills).length > 0 ? (
                    ensureArray(viewingResume.skills).map((s, i) => (
                      <span key={i} className="view-skill-tag">{s}</span>
                    ))
                  ) : (
                    <span className="view-empty">Навыки не указаны</span>
                  )}
                </div>
              </div>
              <div className="view-section">
                <h4>Описание</h4>
                <div className="view-description">{viewingResume.description || '—'}</div>
              </div>
              <div className="view-section">
                <h4>Места работы</h4>
                {ensureArray(viewingResume.workplaces).length > 0 ? (
                  <div className="view-workplaces">
                    {ensureArray(viewingResume.workplaces).map((w, index) => (
                      <div key={w.id || index} className="view-workplace-item">
                        <div className="view-workplace-header">
                          <span className="view-workplace-number">Место {index + 1}</span>
                          <span className="view-workplace-duration">{w.duration_months || 0} месяцев</span>
                        </div>
                        <div className="view-workplace-duties">{w.duties_description || '—'}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="view-empty">Места работы не указаны</span>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={() => { setViewModalOpen(false); handleEdit(viewingResume); }}>
                Редактировать
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setViewModalOpen(false)}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {editModalOpen && editingResume && (
        <div className="modal-overlay" onClick={() => setEditModalOpen(false)}>
          <div className="modal-content parser-edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Редактировать резюме</h3>
            <form onSubmit={handleSaveEdit}>
              <div className="form-group">
                <label>Профессия *</label>
                <input
                  type="text"
                  value={editFormData.profession}
                  onChange={(e) => setEditFormData({ ...editFormData, profession: e.target.value })}
                  required
                  maxLength={255}
                />
              </div>
              <div className="form-group">
                <label>Грейд *</label>
                <select
                  value={editFormData.grade}
                  onChange={(e) => setEditFormData({ ...editFormData, grade: e.target.value })}
                >
                  {GRADES.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Лет опыта *</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={editFormData.years_experience}
                  onChange={(e) => setEditFormData({ ...editFormData, years_experience: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <EditableListField
                  label="Навыки"
                  value={editFormData.skills}
                  onChange={(skills) => setEditFormData({ ...editFormData, skills })}
                  placeholder="Навык"
                  addButtonLabel="+ Добавить навык"
                />
              </div>
              <div className="form-group">
                <label>Описание *</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>
              <div className="form-group">
                <div className="workplaces-header">
                  <label>Места работы</label>
                  <button type="button" className="btn btn-sm" onClick={addWorkplace}>+ Добавить место</button>
                </div>
                {editFormData.workplaces.map((w, index) => (
                  <div key={index} className="workplace-entry">
                    <div className="workplace-row-header">
                      <label className="workplace-label">Место {index + 1}</label>
                      <input
                        type="number"
                        min="1"
                        placeholder="Месяцев"
                        value={w.duration_months}
                        onChange={(e) => updateWorkplace(index, 'duration_months', parseInt(e.target.value, 10) || 1)}
                        className="workplace-duration"
                        aria-label="Продолжительность в месяцах"
                      />
                      <span className="workplace-duration-suffix">мес.</span>
                      <button type="button" className="btn-icon btn-delete" onClick={() => removeWorkplace(index)} title="Удалить место работы">✕</button>
                    </div>
                    <textarea
                      placeholder="Описание обязанностей в компании"
                      value={w.duties_description}
                      onChange={(e) => updateWorkplace(index, 'duties_description', e.target.value)}
                      rows={3}
                      className="workplace-duties"
                      aria-label={`Обязанности, место ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">Сохранить</button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditModalOpen(false)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <h3 className="results-title">Результаты обработки</h3>
      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Профессия</th>
                <th>Грейд</th>
                <th>Лет опыта</th>
                <th>Мест</th>
                <th>Навыки</th>
                <th>Описание</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-state">Нет обработанных резюме. Загрузите PDF и отправьте текст на обработку.</td>
                </tr>
              ) : (
                list.map((r) => (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>{r.profession}</td>
                    <td>{r.grade}</td>
                    <td>{Number(r.years_experience)}</td>
                    <td>{r.workplaces_count}</td>
                    <td className="cell-list">
                      {ensureArray(r.skills).length > 0
                        ? ensureArray(r.skills).map((s, i) => <span key={i} className="list-tag">{s}</span>)
                        : '—'}
                    </td>
                    <td className="cell-description">{r.description ? (r.description.slice(0, 80) + (r.description.length > 80 ? '…' : '')) : '—'}</td>
                    <td>
                      <button type="button" className="btn-icon btn-view" onClick={() => handleView(r)} title="Просмотр">👁️</button>
                      <button type="button" className="btn-icon btn-edit" onClick={() => handleEdit(r)} title="Редактировать">✏️</button>
                      <button type="button" className="btn-icon btn-delete" onClick={() => handleDelete(r.id)} title="Удалить">✕</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ResumeParserSubTab;
